import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, ChevronDown, ChevronRight, Clock, CheckCircle,
  XCircle, FileText, Download, Trash2, MessageSquare, Send,
  RefreshCw, AlertCircle, Edit2, X, Calendar, Paperclip, Image
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { resolveFileUrl } from '@/lib/utils';
import { GradeHeroCard, ScoreCard, FullReport, IntelAnalysisBox } from '../components/LOIVerificationComponents';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const isImageFile = (filename) => /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(filename);

const AdminQuoteRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMainAdmin, user } = useAuth();
  const isSuperAdmin = isMainAdmin();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [newNotes, setNewNotes] = useState({});
  const [noteAttachments, setNoteAttachments] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [denyReason, setDenyReason] = useState({});
  const [showDenyModal, setShowDenyModal] = useState(null);
  const [loiVerifications, setLoiVerifications] = useState({});
  const noteFileRefs = React.useRef({});

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      const res = await api.get(`/quote-process/requests/${id}`);
      setRequest(res.data);
      // Expand first step by default
      if (res.data.detailed_steps?.length > 0) {
        setExpandedSteps({ [res.data.detailed_steps[0].step_id]: true });
      }
      // Load LOI verifications for step 1 documents
      for (const step of res.data.detailed_steps || []) {
        if (step.order_number === 1 && step.documents?.length > 0) {
          for (const doc of step.documents) {
            if (doc.loi_verification_id) {
              try {
                const verifRes = await api.get(`/loi-verification/verifications/${doc.loi_verification_id}`);
                if (verifRes.data?.result) {
                  setLoiVerifications(prev => ({
                    ...prev,
                    [step.step_id]: {
                      verification_id: doc.loi_verification_id,
                      summary: verifRes.data.result.summary,
                      grading: verifRes.data.result.grading,
                      checks: verifRes.data.result.checks,
                      contact_info: verifRes.data.result.contact_info,
                      metadata: verifRes.data.result.metadata,
                      intel_analysis: verifRes.data.result.intel_analysis,
                      is_paid_user: true,
                      partial: false,
                    }
                  }));
                }
              } catch (e) {
                // Skip if not accessible
              }
            }
          }
        }
      }
    } catch (error) {
      toast.error('Failed to load quote request');
      navigate('/admin/quote-requests');
    } finally {
      setLoading(false);
    }
  };

  const toggleStep = (stepId) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  const handleApprove = async (stepId) => {
    setActionLoading(prev => ({ ...prev, [stepId]: true }));
    try {
      await api.put(`/admin/quote-process/requests/${id}/steps/${stepId}/status`, {
        status: 'approved'
      });
      toast.success('Step approved');
      fetchRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve step');
    } finally {
      setActionLoading(prev => ({ ...prev, [stepId]: false }));
    }
  };

  const handleDeny = async (stepId) => {
    setActionLoading(prev => ({ ...prev, [stepId]: true }));
    try {
      await api.put(`/admin/quote-process/requests/${id}/steps/${stepId}/status`, {
        status: 'denied',
        admin_notes: denyReason[stepId] || ''
      });
      toast.success('Step denied - user has been notified');
      setShowDenyModal(null);
      setDenyReason(prev => ({ ...prev, [stepId]: '' }));
      fetchRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny step');
    } finally {
      setActionLoading(prev => ({ ...prev, [stepId]: false }));
    }
  };

  const handleDeleteDocument = async (stepId, docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api.delete(`/admin/quote-process/requests/${id}/steps/${stepId}/documents/${docId}`);
      toast.success('Document deleted');
      fetchRequest();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const updateStepNotes = (stepId, updater) => {
    setRequest(prev => ({
      ...prev,
      detailed_steps: prev.detailed_steps.map(step =>
        step.step_id === stepId ? { ...step, notes: updater(step.notes || []) } : step
      )
    }));
  };

  const handleAddNote = async (stepId) => {
    const content = newNotes[stepId]?.trim();
    if (!content) return;

    const file = noteAttachments[stepId];
    setActionLoading(prev => ({ ...prev, [`note_${stepId}`]: true }));
    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append('content', content);
        formData.append('file', file);
        res = await api.post(`/quote-process/requests/${id}/steps/${stepId}/notes/with-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await api.post(`/quote-process/requests/${id}/steps/${stepId}/notes`, { content });
      }
      const newNote = {
        id: res.data.id,
        content,
        author_id: user?.id,
        author_name: res.data.author_name,
        is_admin: res.data.is_admin,
        is_super_admin: res.data.is_super_admin,
        attachment: res.data.attachment || null,
        created_at: res.data.created_at,
      };
      updateStepNotes(stepId, notes => [...notes, newNote]);
      toast.success('Note added');
      setNewNotes(prev => ({ ...prev, [stepId]: '' }));
      setNoteAttachments(prev => ({ ...prev, [stepId]: null }));
      if (noteFileRefs.current[stepId]) noteFileRefs.current[stepId].value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setActionLoading(prev => ({ ...prev, [`note_${stepId}`]: false }));
    }
  };

  const canEditNote = (note) => {
    if (isSuperAdmin) return true;
    if (note.author_id !== user?.id) return false;
    const created = new Date(note.created_at);
    const elapsed = (Date.now() - created.getTime()) / 1000;
    return elapsed <= 60;
  };

  const canDeleteNote = () => {
    return isSuperAdmin;
  };

  const handleEditNote = async (stepId, noteId) => {
    if (!editNoteContent.trim()) return;

    try {
      await api.put(`/admin/quote-process/requests/${id}/steps/${stepId}/notes/${noteId}`, {
        content: editNoteContent
      });
      updateStepNotes(stepId, notes =>
        notes.map(n => n.id === noteId ? { ...n, content: editNoteContent, edited: true } : n)
      );
      toast.success('Note updated');
      setEditingNote(null);
      setEditNoteContent('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update note');
    }
  };

  const handleDeleteNote = async (stepId, noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await api.delete(`/admin/quote-process/requests/${id}/steps/${stepId}/notes/${noteId}`);
      updateStepNotes(stepId, notes => notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete note');
    }
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case 'approved':
        return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: CheckCircle };
      case 'denied':
        return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: XCircle };
      case 'pending':
        return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', icon: Clock };
      default:
        return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-500', icon: Clock };
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  if (!request) return null;

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/quote-requests')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Quote Requests
          </button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Quote Request #{id?.slice(-8)}</h1>
              <p className="text-gray-500 text-sm mt-1">Review and manage this quote submission</p>
            </div>
            <div className={`px-4 py-2 rounded-xl text-sm font-medium ${
              request.status === 'completed' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-blue-100 text-blue-700'
            }`}>
              {request.status?.replace('_', ' ')}
            </div>
          </div>
        </div>

        {/* User Info Card */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <User className="w-6 h-6 text-gray-500" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{request.user_name || 'Unknown User'}</p>
              <p className="text-sm text-gray-500">{request.user_email}</p>
              {isSuperAdmin && request.company_name && (
                <p className="text-xs text-blue-600 mt-0.5">Company: {request.company_name}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Created</p>
              <p className="text-sm text-gray-700">{new Date(request.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {request.detailed_steps?.map((step, index) => {
            const statusStyles = getStatusStyles(step.status);
            const StatusIcon = statusStyles.icon;
            const isExpanded = expandedSteps[step.step_id];
            const canApprove = step.status === 'pending';

            return (
              <div 
                key={step.step_id}
                className={`rounded-xl border overflow-hidden ${statusStyles.border}`}
                style={{ backgroundColor: step.status === 'pending' ? '#FEFCE8' : step.status === 'approved' ? '#F0FDF4' : step.status === 'denied' ? '#FEF2F2' : '#FFFFFF' }}
                data-testid={`step-${step.step_id}`}
              >
                {/* Step Header */}
                <div 
                  className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-black/5 transition"
                  onClick={() => toggleStep(step.step_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                      <span className="text-sm font-semibold text-gray-500">Step {step.order_number}</span>
                    </div>
                    <h3 className="font-medium text-gray-900">{step.name}</h3>
                    {!step.is_required && (
                      <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">Optional</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-bold text-sm uppercase tracking-wide ${statusStyles.text}`}>
                      {step.status === 'not_started' ? 'Not Started' : step.status}
                    </span>
                    <StatusIcon className={`w-5 h-5 ${statusStyles.text}`} />
                  </div>
                </div>

                {/* Step Content */}
                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-4 bg-white">
                    {/* Questions & Answers */}
                    {step.questions?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Responses ({step.questions.length})
                        </h4>
                        <div className="space-y-3">
                          {step.questions.map((q) => {
                            const answer = step.answers?.[q.id];
                            return (
                              <div key={q.id} className="bg-gray-50 rounded-lg p-3">
                                <p className="text-xs font-medium text-gray-500 mb-1">
                                  {q.question_text}
                                  {q.is_required && <span className="text-red-500 ml-1">*</span>}
                                </p>
                                {q.field_type === 'file' && answer ? (
                                  <a
                                    href={resolveFileUrl(answer, process.env.REACT_APP_BACKEND_URL)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm font-medium hover:underline"
                                    style={{ color: BRAND_BLUE }}
                                  >
                                    View uploaded file
                                  </a>
                                ) : q.field_type === 'note' ? (
                                  <p className="text-sm text-blue-700 bg-blue-50 rounded px-2 py-1">{q.question_text}</p>
                                ) : q.field_type === 'checkbox' && answer ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {answer.split('|||').map((val, i) => (
                                      <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {val}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-900">
                                    {answer || <span className="text-gray-400 italic">No answer provided</span>}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Documents - always show for admin if there are any, or if step allows uploads */}
                    {(step.documents?.length > 0 || step.allow_upload_documents) && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Documents ({step.documents?.length || 0})
                        </h4>
                        {step.documents?.length > 0 ? (
                          <div className="space-y-2">
                            {step.documents.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center gap-3">
                                  <FileText className="w-5 h-5 text-gray-400" />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{doc.file_name}</p>
                                    <p className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <a
                                    href={resolveFileUrl(doc.file_url, process.env.REACT_APP_BACKEND_URL)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                    title="View"
                                  >
                                    <Download className="w-4 h-4" />
                                  </a>
                                  <button
                                    onClick={() => handleDeleteDocument(step.step_id, doc.id)}
                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">No documents uploaded</p>
                        )}

                        {/* LOI Verification Report (Step 1) */}
                        {loiVerifications[step.step_id] && !loiVerifications[step.step_id].error && (
                          <div className="mt-4 space-y-4">
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              LOI Verification Report
                            </h4>

                            <GradeHeroCard result={loiVerifications[step.step_id]} />

                            {loiVerifications[step.step_id].grading && (
                              <div className="grid grid-cols-3 gap-3">
                                <ScoreCard label="Must Have" value={loiVerifications[step.step_id].grading.level_1} color="red" />
                                <ScoreCard label="Important" value={loiVerifications[step.step_id].grading.level_2} color="orange" />
                                <ScoreCard label="Nice to Have" value={loiVerifications[step.step_id].grading.level_3} color="blue" />
                              </div>
                            )}

                            {/* Download Verification Report */}
                            {loiVerifications[step.step_id].verification_id && (
                              <button
                                onClick={async () => {
                                  try {
                                    toast.info('Generating report...');
                                    const res = await api.get(`/loi-verification/report/${loiVerifications[step.step_id].verification_id}`, {
                                      responseType: 'blob',
                                      timeout: 120000,
                                    });
                                    const blob = new Blob([res.data], { type: 'application/pdf' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = 'LOI_Verified_Report.pdf';
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    window.URL.revokeObjectURL(url);
                                    toast.success('Report downloaded');
                                  } catch (err) {
                                    console.error('Report download error:', err);
                                    toast.error('Failed to download report');
                                  }
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition"
                                style={{ backgroundColor: BRAND_BLUE }}
                              >
                                <Download className="w-4 h-4" />
                                Download Verified LOI Report (PDF)
                              </button>
                            )}

                            {loiVerifications[step.step_id].intel_analysis && (
                              <IntelAnalysisBox
                                intel={loiVerifications[step.step_id].intel_analysis}
                                isPaidUser={true}
                              />
                            )}

                            <FullReport
                              result={loiVerifications[step.step_id]}
                              isPaidUser={true}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Discussion / Questions and Document Revisions ({step.notes?.length || 0})
                      </h4>
                      <div className="space-y-3 mb-4">
                        {step.notes?.length > 0 ? (
                          step.notes.map((note) => (
                            <div
                              key={note.id}
                              className={`rounded-lg p-3 ${note.is_super_admin ? 'bg-orange-50' : note.is_admin ? 'bg-green-50' : 'bg-yellow-50'}`}
                            >
                              {editingNote === note.id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={editNoteContent}
                                    onChange={(e) => setEditNoteContent(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                                    rows={3}
                                  />
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={() => { setEditingNote(null); setEditNoteContent(''); }}
                                      className="px-3 py-1.5 text-gray-600 text-sm"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => handleEditNote(step.step_id, note.id)}
                                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm"
                                    >
                                      Save
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-700">{note.author_name}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-gray-500">
                                        {new Date(note.created_at).toLocaleString()}
                                      </span>
                                      {canEditNote(note) && (
                                        <button
                                          onClick={() => { setEditingNote(note.id); setEditNoteContent(note.content); }}
                                          className="p-1 text-gray-400 hover:text-gray-600"
                                          title={isSuperAdmin ? 'Edit' : 'Edit (within 60s)'}
                                        >
                                          <Edit2 className="w-3 h-3" />
                                        </button>
                                      )}
                                      {canDeleteNote() && (
                                        <button
                                          onClick={() => handleDeleteNote(step.step_id, note.id)}
                                          className="p-1 text-gray-400 hover:text-red-600"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-700">{note.content}</p>
                                  {note.edited && <p className="text-xs text-gray-400 mt-1">(edited)</p>}
                                  {note.attachment && (
                                    <div className="mt-2">
                                      {isImageFile(note.attachment.file_name) ? (
                                        <a href={note.attachment.file_url} target="_blank" rel="noopener noreferrer">
                                          <img
                                            src={note.attachment.file_url}
                                            alt={note.attachment.file_name}
                                            className="max-w-xs max-h-48 rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
                                          />
                                          <span className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                            <Image className="w-3 h-3" />
                                            {note.attachment.file_name}
                                          </span>
                                        </a>
                                      ) : (
                                        <a
                                          href={note.attachment.file_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 bg-white/60 rounded px-2 py-1 w-fit"
                                        >
                                          <Paperclip className="w-3 h-3" />
                                          {note.attachment.file_name}
                                          <Download className="w-3 h-3" />
                                        </a>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-400 italic">No notes yet</p>
                        )}
                      </div>

                      {/* Add Note with optional attachment */}
                      <div className="space-y-2">
                        {noteAttachments[step.step_id] && (
                          <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                            <Paperclip className="w-3 h-3" />
                            <span className="truncate">{noteAttachments[step.step_id].name}</span>
                            <button
                              onClick={() => {
                                setNoteAttachments(prev => ({ ...prev, [step.step_id]: null }));
                                if (noteFileRefs.current[step.step_id]) noteFileRefs.current[step.step_id].value = '';
                              }}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newNotes[step.step_id] || ''}
                            onChange={(e) => setNewNotes(prev => ({ ...prev, [step.step_id]: e.target.value }))}
                            placeholder="Add a note or question..."
                            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2"
                            onKeyPress={(e) => e.key === 'Enter' && !noteAttachments[step.step_id] && handleAddNote(step.step_id)}
                          />
                          <input
                            type="file"
                            ref={el => noteFileRefs.current[step.step_id] = el}
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                setNoteAttachments(prev => ({ ...prev, [step.step_id]: e.target.files[0] }));
                              }
                            }}
                          />
                          <button
                            onClick={() => noteFileRefs.current[step.step_id]?.click()}
                            className="px-3 py-2 border border-gray-200 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                            title="Attach document"
                          >
                            <Paperclip className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleAddNote(step.step_id)}
                            disabled={actionLoading[`note_${step.step_id}`]}
                            className="px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                            style={{ backgroundColor: BRAND_BLUE }}
                          >
                            {actionLoading[`note_${step.step_id}`] ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Approval Actions */}
                    {canApprove && (
                      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                        <button
                          onClick={() => setShowDenyModal(step.step_id)}
                          disabled={actionLoading[step.step_id]}
                          className="flex items-center gap-2 px-5 py-2.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl text-sm font-medium transition disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4" />
                          Deny
                        </button>
                        <button
                          onClick={() => handleApprove(step.step_id)}
                          disabled={actionLoading[step.step_id]}
                          className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white hover:bg-green-700 rounded-xl text-sm font-medium transition disabled:opacity-50"
                        >
                          {actionLoading[step.step_id] ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                          Approve
                        </button>
                      </div>
                    )}

                    {/* Admin notes for denied step */}
                    {step.status === 'denied' && step.admin_notes && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs font-medium text-red-700 mb-1">Denial Reason:</p>
                        <p className="text-sm text-red-600">{step.admin_notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Deny Modal */}
        {showDenyModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Deny Step</h3>
                <button onClick={() => setShowDenyModal(null)} className="p-1 text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Please provide a reason for denying this step. The user will receive this feedback.
                </p>
                <textarea
                  value={denyReason[showDenyModal] || ''}
                  onChange={(e) => setDenyReason(prev => ({ ...prev, [showDenyModal]: e.target.value }))}
                  placeholder="Enter reason for denial..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2"
                  rows={4}
                />
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => setShowDenyModal(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeny(showDenyModal)}
                  disabled={actionLoading[showDenyModal]}
                  className="flex items-center gap-2 px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-medium disabled:opacity-50"
                >
                  {actionLoading[showDenyModal] ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  Deny Step
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminQuoteRequestDetail;
